import React, { useEffect, useState } from 'react';
import { Button, Card, Modal, Form, Input, Upload, Image, Space, Typography, Popconfirm, message, Row, Col, Spin, Tabs, DatePicker } from 'antd';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import AdminNav from './AdminNav';
import moment from 'moment';
import {
  fetchNewsletters as fetchNewslettersApi,
  createNewsletter,
  updateNewsletter,
  deleteNewsletter,
  uploadNewsletterImage,
  deleteNewsletterImage,
  fetchNewsletter,
  fetchNews,
  createNews,
  updateNews,
  deleteNews,
  uploadNewsImage,
  deleteNewsImage,
} from './api';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

const AdminNewsletters = (props) => {
  const [newsletters, setNewsletters] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('newsletters');
  
  // Newsletter Modal State
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);
  
  // News Modal State
  const [openNewsModal, setOpenNewsModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [newsForm] = Form.useForm();
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [descHtml, setDescHtml] = useState('');
  
  const navigate = useNavigate();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: true, autolink: true }),
    ],
    content: descHtml || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setDescHtml(html);
      form.setFieldsValue({ description: html });
    },
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'newsletters') {
        const list = await fetchNewslettersApi();
        setNewsletters(list.map((n, idx) => ({ key: n.id || idx, ...n })));
      } else {
        const list = await fetchNews;
        const data = await fetchNews();
        setNewsList(data.map((n, idx) => ({ key: n.id || idx, ...n })));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const generateSlug = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  };

  // Newsletter Handlers
  const onCreate = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setDescHtml('');
    form.resetFields();
    setOpenModal(true);
  };

  const onEdit = async (record) => {
    setEditing(record);
    setImageFile(null);
    setImagePreview(null);
    form.setFieldsValue({ heading: record.heading, description: record.description, source: record.source, slug: record.slug });
    setDescHtml(record.description || '');
    setOpenModal(true);
    try {
      const idOrSlug = record.id || record.slug || generateSlug(record.heading);
      const detail = await fetchNewsletter(idOrSlug);
      if (detail) {
        setEditing((prev) => ({ ...prev, ...detail }));
        form.setFieldsValue({
          heading: detail.heading ?? record.heading,
          description: detail.description ?? record.description,
          source: detail.source ?? record.source,
          slug: detail.slug ?? record.slug
        });
        setDescHtml(detail.description ?? record.description ?? '');
      }
    } catch (e) {}
  };

  const onDelete = async (record) => {
    try {
      const ok = await deleteNewsletter({ id: record.id });
      if (!ok) throw new Error('Delete failed');
      message.success('Newsletter deleted');
      fetchData();
    } catch (e) {
      message.error('Delete failed');
    }
  };

  // News Handlers
  const onCreateNews = () => {
    setEditingNews(null);
    setImageFile(null);
    setImagePreview(null);
    newsForm.resetFields();
    setOpenNewsModal(true);
  };

  const onEditNews = (record) => {
    setEditingNews(record);
    setImageFile(null);
    setImagePreview(record.imageUrl || null);
    newsForm.setFieldsValue({
      title: record.title,
      description: record.description,
      link: record.link,
      date: record.date ? moment(record.date) : null
    });
    setOpenNewsModal(true);
  };

  const onDeleteNews = async (record) => {
    try {
      const ok = await deleteNews({ id: record.id });
      if (!ok) throw new Error('Delete failed');
      message.success('News item deleted');
      fetchData();
    } catch (e) {
      message.error('Delete failed');
    }
  };

  const uploadImageIfNeeded = async (type) => {
    if (!imageFile) {
      if (type === 'newsletters') return { url: editing?.imageUrl || '', key: editing?.imageKey || '' };
      return { url: editingNews?.imageUrl || '', key: editingNews?.imageKey || '' };
    }
    
    if (type === 'newsletters') {
      return await uploadNewsletterImage(imageFile);
    } else {
      return await uploadNewsImage(imageFile);
    }
  };

  const onSubmit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const values = await form.validateFields();
      if (editing && imageFile && editing.imageKey) {
        await deleteNewsletterImage(editing.imageKey);
      }
      const { url, key } = await uploadImageIfNeeded('newsletters');
      const payload = { heading: values.heading, description: descHtml || values.description || '', imageUrl: url, imageKey: key, source: values.source || '', slug: values.slug };
      
      if (editing) {
        const ok = await updateNewsletter({ id: editing.id, ...payload });
        if (!ok) throw new Error('Update failed');
        message.success('Newsletter updated');
      } else {
        const ok = await createNewsletter(payload);
        if (!ok) throw new Error('Create failed');
        message.success('Newsletter created');
      }
      setOpenModal(false);
      fetchData();
    } catch (e) {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onSubmitNews = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const values = await newsForm.validateFields();
      if (editingNews && imageFile && editingNews.imageKey) {
        await deleteNewsImage(editingNews.imageKey);
      }
      const { url, key } = await uploadImageIfNeeded('news');
      const payload = {
        title: values.title,
        description: values.description,
        imageUrl: url,
        imageKey: key,
        link: values.link,
        date: values.date ? values.date.format('MMMM D, YYYY') : moment().format('MMMM D, YYYY')
      };
      
      if (editingNews) {
        const ok = await updateNews({ id: editingNews.id, ...payload });
        if (!ok) throw new Error('Update failed');
        message.success('News updated');
      } else {
        const ok = await createNews(payload);
        if (!ok) throw new Error('Create failed');
        message.success('News created');
      }
      setOpenNewsModal(false);
      fetchData();
    } catch (e) {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <AdminNav {...props}/>
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>Newsletter & News</Typography.Title>
          <div style={{ marginLeft: 'auto' }}>
            {activeTab === 'newsletters' ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>New Newsletter</Button>
            ) : (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreateNews}>New News Item</Button>
            )}
          </div>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 12 }}>
          <Tabs.TabPane tab="Newsletters" key="newsletters">
            <Input.Search
              placeholder="Search newsletters"
              allowClear
              style={{ maxWidth: 600, marginBottom: 16 }}
            />
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {newsletters.map((item) => (
                  <Col key={item.key} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      cover={item.imageUrl ? <img alt={item.heading} src={item.imageUrl} style={{ height: 180, objectFit: 'cover' }} /> : null}
                      onClick={() => navigate(`/admin/newsletter/${item.slug || generateSlug(item.heading) || item.id || item.key}`)}
                    >
                      <Typography.Text type="secondary">{item.date}</Typography.Text>
                      <Typography.Title level={5} style={{ marginTop: 8 }}>{item.heading}</Typography.Title>
                      <Typography.Paragraph ellipsis={{ rows: 2 }}>{item.description}</Typography.Paragraph>
                      <Space>
                        <Button type="link" onClick={(e) => { e.stopPropagation(); onEdit(item); }}>Edit</Button>
                        <Popconfirm title="Delete this newsletter?" onConfirm={(e) => { e?.stopPropagation?.(); onDelete(item); }}>
                          <Button danger type="link" onClick={(e) => e.stopPropagation()}>Delete</Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tabs.TabPane>

          <Tabs.TabPane tab="Plootus in the News" key="news">
            <Input.Search
              placeholder="Search news articles"
              allowClear
              style={{ maxWidth: 600, marginBottom: 16 }}
            />
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {newsList.map((item) => (
                  <Col key={item.key} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      cover={item.imageUrl ? <img alt={item.title} src={item.imageUrl} style={{ height: 180, objectFit: 'cover' }} /> : null}
                    >
                      <Typography.Text type="secondary">{item.date}</Typography.Text>
                      <Typography.Title level={5} style={{ marginTop: 8 }}>{item.title}</Typography.Title>
                      <Typography.Paragraph ellipsis={{ rows: 3 }}>{item.description}</Typography.Paragraph>
                      <Typography.Link href={item.link} target="_blank" style={{ display: 'block', marginBottom: 12 }}>View Source</Typography.Link>
                      <Space>
                        <Button type="link" onClick={() => onEditNews(item)}>Edit</Button>
                        <Popconfirm title="Delete this news item?" onConfirm={() => onDeleteNews(item)}>
                          <Button danger type="link">Delete</Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tabs.TabPane>
        </Tabs>

        {/* Newsletter Modal */}
        <Modal
          open={openModal}
          onCancel={() => setOpenModal(false)}
          onOk={onSubmit}
          okText={editing ? 'Update' : 'Create'}
          title={editing ? 'Edit Newsletter' : 'Create Newsletter'}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form 
            form={form} 
            layout="vertical"
            onValuesChange={(changedValues) => {
              if (changedValues.heading) {
                const slug = generateSlug(changedValues.heading);
                form.setFieldsValue({ slug });
              }
            }}
          >
            <Form.Item name="heading" label="Heading" rules={[{ required: true }]}> 
              <Input placeholder="Enter newsletter heading" />
            </Form.Item>
            <Form.Item name="slug" label="Slug" rules={[{ required: true }]}> 
              <Input placeholder="Enter url slug" />
            </Form.Item>
            <Form.Item name="source" label="Source">
              <Input placeholder="Enter source (e.g., Bloomberg)" />
            </Form.Item>
            <Form.Item label="Description" required> 
              <div style={{ border: '1px solid #e5e5e5', borderRadius: 6 }}>
                <div style={{ padding: 8, borderBottom: '1px solid #e5e5e5', display: 'flex', gap: 8 }}>
                  <Button size="small" onClick={() => editor?.chain().focus().toggleBold().run()}>Bold</Button>
                  <Button size="small" onClick={() => editor?.chain().focus().toggleItalic().run()}>Italic</Button>
                  <Button size="small" onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>H3</Button>
                  <Button size="small" onClick={() => editor?.chain().focus().toggleBulletList().run()}>• List</Button>
                  <Button size="small" onClick={() => {
                    const url = prompt('Enter URL');
                    if (url) editor?.chain().focus().setLink({ href: url }).run();
                  }}>Link</Button>
                </div>
                <div style={{ padding: 8, minHeight: 120 }}>
                  <EditorContent editor={editor} />
                </div>
              </div>
            </Form.Item>
            <Form.Item name="description" rules={[{ required: true, message: 'Description is required' }]} style={{ display: 'none' }}>
              <Input />
            </Form.Item>
            <Form.Item label="Image">
              <Upload
                accept="image/*"
                beforeUpload={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); return false; }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Image</Button>
              </Upload>
              {imagePreview && <Image src={imagePreview} width={160} style={{ marginTop: 12 }} />}
              {!imagePreview && editing?.imageUrl && <Image src={editing.imageUrl} width={160} style={{ marginTop: 12 }} />}
            </Form.Item>
          </Form>
        </Modal>

        {/* News Modal */}
        <Modal
          open={openNewsModal}
          onCancel={() => setOpenNewsModal(false)}
          onOk={onSubmitNews}
          okText={editingNews ? 'Update' : 'Create'}
          title={editingNews ? 'Edit News Item' : 'Create News Item'}
          confirmLoading={saving}
          width={800}
          destroyOnClose
        >
          <Form form={newsForm} layout="vertical">
            <Form.Item name="title" label="Title" rules={[{ required: true }]}> 
              <Input placeholder="Enter article title" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true }]}> 
              <Input.TextArea rows={6} placeholder="Enter article summary or description" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="date" label="Publication Date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} format="MMMM D, YYYY" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="link" label="Article Link" rules={[{ required: true, type: 'url' }]}>
                  <Input placeholder="https://..." />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label="Image">
              <Upload
                accept="image/*"
                beforeUpload={(file) => { setImageFile(file); setImagePreview(URL.createObjectURL(file)); return false; }}
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Image</Button>
              </Upload>
              {imagePreview && <Image src={imagePreview} width={240} style={{ marginTop: 12 }} />}
              {!imagePreview && editingNews?.imageUrl && <Image src={editingNews.imageUrl} width={240} style={{ marginTop: 12 }} />}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default AdminNewsletters;
